import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import * as LucideIcons from 'lucide-react';
import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';
import { useTranslation } from 'react-i18next';

// Fix for default Leaflet icons
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const JourneyTab = ({ whispers, t }) => {
    const [view, setView] = useState('map'); // 'map' or 'timeline'
    const [locationWhispers, setLocationWhispers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Filter whispers that have location data
        const whispersWithLocation = whispers.filter(w => w.location?.latitude && w.location?.longitude);
        setLocationWhispers(whispersWithLocation);
        setIsLoading(false);
    }, [whispers]);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (locationWhispers.length === 0 && view === 'map') {
        return <p className="text-center text-gray-400 italic py-8">{t('userProfile.journey.noLocationWhispers')}</p>;
    }

    if (whispers.length === 0 && view === 'timeline') {
        return <p className="text-center text-gray-400 italic py-8">{t('userProfile.journey.noWhispers')}</p>;
    }

    const TimelineView = () => (
        <div className="space-y-8 relative before:absolute before:inset-y-0 before:w-0.5 before:bg-gray-700 before:left-3">
            {whispers.map((whisper, index) => (
                <div key={whisper.id} className="flex items-center relative">
                    <div className="hidden md:block absolute left-0 w-24 text-right pr-8 text-gray-400 text-sm">
                        {whisper.timestamp?.toDate().toLocaleDateString()}
                    </div>
                    <div className="z-10 w-6 h-6 rounded-full bg-blue-500 shadow-md flex items-center justify-center text-white text-xs font-bold left-0 -ml-3">
                        <LucideIcons.MessageSquare size={16} />
                    </div>
                    <div className="flex-grow p-4 ml-6 bg-gray-800/50 rounded-lg shadow-lg border border-gray-700">
                        <p className="text-gray-200 text-lg">{whisper.content}</p>
                        {whisper.location && <p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><LucideIcons.MapPin size={12} /> {whisper.location.name}</p>}
                    </div>
                </div>
            ))}
        </div>
    );

    const MapView = () => (
        <div className="h-96 w-full rounded-lg overflow-hidden">
            <MapContainer center={[locationWhispers[0].location.latitude, locationWhispers[0].location.longitude]} zoom={6} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {locationWhispers.map(whisper => (
                    <Marker key={whisper.id} position={[whisper.location.latitude, whisper.location.longitude]}>
                        <Popup>
                            <div className="text-black">
                                <p className="font-bold">{whisper.content}</p>
                                <p className="text-sm">{whisper.timestamp?.toDate().toLocaleDateString()}</p>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );

    return (
        <div className="p-4">
            <div className="flex justify-center mb-4">
                <button onClick={() => setView('map')} className={`px-4 py-2 rounded-full ${view === 'map' ? 'bg-blue-500 text-white' : 'bg-gray-700'}`}>{t('userProfile.journey.mapView')}</button>
                <button onClick={() => setView('timeline')} className={`ml-2 px-4 py-2 rounded-full ${view === 'timeline' ? 'bg-blue-500 text-white' : 'bg-gray-700'}`}>{t('userProfile.journey.timelineView')}</button>
            </div>
            {view === 'map' ? <MapView /> : <TimelineView />}
        </div>
    );
};

export default JourneyTab;
